import { useToast } from "@expert/common-ui";
import { Button, Flex, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { logger } from "@soluto-private/eventualize/dist/logging";
import type { JSX } from "react";
import { useAnalytics } from "@/analytics/hooks/useAnalytics";
import { FTCDisclosureModal } from "@/sales-tools/disclosures/components/FTCDisclosureModal";
import { useMostRecentTask, useSessionId } from "@/sdk";
import type { EligibleProduct } from "../../stores";
import { useIntegratedFlowStore } from "../../stores/useIntegratedFlowStore";
import EnrollmentWait from "../Icons/EnrollmentWait.svg";
import classes from "./WaitForEnrollment.module.css";

interface WaitForEnrollmentProps {
    product: EligibleProduct;
    shouldDisableSendLink: boolean;
}

export function WaitForEnrollment({ product, shouldDisableSendLink }: WaitForEnrollmentProps): JSX.Element | null {
    const sessionId = useSessionId();
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);
    const { dispatcher } = useAnalytics();
    const toast = useToast();
    const [opened, { open, close }] = useDisclosure(false);
    const task = useMostRecentTask();
    const language = task?.locale === "es-mx" ? "es-US" : "en-US";

    const returnToSendLink = () => {
        try {
            updateIntegratedStep(sessionId, "SendLinkStep");
            void dispatcher
                .withExtra({
                    nextStep: integratedStep,
                })
                .dispatchBusinessEvent("ButtonClicked_ReSendLink");
        } catch (error) {
            toast.error("Error returning to send link");
            logger.error({ error }, "Error returning to send link");
        }
    };

    return (
        <Flex direction="column" align="center" pl="xl" pr="xl" w="100%">
            <Flex pb="md" pt="xxl">
                <img src={EnrollmentWait} height={150} />
            </Flex>
            <Flex fz="lg" fw={700}>
                Waiting on them to enroll
            </Flex>
            <Flex fz="sm" pt="md" pb="xxl">
                Heads up—this link will expire in 30 minutes.
            </Flex>
            <Tooltip label="The link can only be sent 3 times" position="bottom" disabled={!shouldDisableSendLink}>
                <Button
                    color="dark.5"
                    className={shouldDisableSendLink ? classes.disabledButton : classes.enabledButton}
                    onClick={() => {
                        void returnToSendLink();
                    }}
                    disabled={shouldDisableSendLink}
                >
                    Re-send link
                </Button>
            </Tooltip>

            <Button variant="subtle" color="white" onClick={open} mt="sm">
                <Text td="underline" inherit>
                    Preview Disclosures
                </Text>
            </Button>
            <FTCDisclosureModal
                opened={opened}
                close={close}
                productSku={product.productSku}
                language={language}
                requiresNyDisclosures={product.requiresNyDisclosures ?? false}
            />
        </Flex>
    );
}
